import { useState } from 'react';

/**
 * Minimum-TAT notice for the indent form.
 *
 * The date picker refuses anything earlier than the floor; this explains why,
 * in the operations team's own terms — every stage, its owner and its budgeted
 * days. Sales pushing for a sooner date can see exactly which step would have
 * to collapse, which is a better conversation than "the system won't let me".
 *
 * The schedule is served by GET /api/procurement/indents/tat-plan so it is
 * never restated here.
 */

export type TatStage = {
  stage: string;
  what: string;
  designation: string;
  person: string;
  days: number;
  note?: string;
};

export type TatPlan = {
  indentType: 'Domestic' | 'Import';
  minDays: number;
  stages: TatStage[];
};

/** "2026-09-09" → "09 Sep 2026". Falls back to the raw string. */
export function prettyDate(iso: string): string {
  // Plain YYYY-MM-DD is a calendar date, not an instant — read it back in UTC
  // so a date computed in IST can't display as the day before.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
      timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric',
    });
  }
  // External sign-offs carry a full timestamp — shown in IST, which is when
  // the person actually signed.
  const dt = new Date(iso);
  if (!isNaN(dt.getTime())) {
    return dt.toLocaleDateString('en-GB', {
      timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    });
  }
  return iso;
}

export default function TatPlanNotice({
  plan, earliest, violation,
}: {
  plan: TatPlan;
  earliest: string;
  /** Set when the current form values break the floor — turns the notice into an error. */
  violation?: string;
}) {
  const [open, setOpen] = useState(false);
  const bad = !!violation;

  return (
    <div
      className={
        'rounded-lg border px-3 py-2 text-[12px] ' +
        (bad ? 'border-rose-300 bg-rose-50 text-rose-900' : 'border-blue-200 bg-blue-50 text-blue-900')
      }
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold">
          {bad ? 'Delivery date is not achievable' : `Minimum ${plan.minDays}-day turnaround`}
        </span>
        <span className="opacity-80">
          {bad ? violation : <>Earliest deliverable date is <strong>{prettyDate(earliest)}</strong>.</>}
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={
            'ml-auto rounded px-2 py-0.5 text-[11px] font-semibold underline-offset-2 hover:underline ' +
            (bad ? 'text-rose-800' : 'text-blue-800')
          }
        >
          {open ? 'Hide breakdown' : 'Why?'}
        </button>
      </div>

      {open && (
        <div className="mt-2 overflow-x-auto rounded border border-white/70 bg-white/70">
          <table className="w-full min-w-[30rem] text-left text-[11px]">
            <thead className="bg-white/80 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5 font-semibold">Stage</th>
                <th className="px-2 py-1.5 font-semibold">What happens</th>
                <th className="px-2 py-1.5 font-semibold">Owner</th>
                <th className="px-2 py-1.5 text-right font-semibold">Days</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {plan.stages.map((s) => (
                <tr key={s.stage} className="border-t border-slate-200/70">
                  <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[10px] text-slate-500">{s.stage}</td>
                  <td className="px-2 py-1.5">
                    {s.what}
                    {s.note && <span className="ml-1 text-[10px] italic text-slate-400">({s.note})</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="font-medium">{s.person}</span>
                    <span className="ml-1 text-[10px] text-slate-400">{s.designation}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {s.days > 0 ? s.days : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-white/80 font-semibold">
                <td className="px-2 py-1.5" colSpan={3}>Total</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{plan.minDays} days</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
